// src/pages/Home/ItemStoreMapping.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import config from '../../config';

const UNITS = ['ml', 'ltr', 'gm', 'kg', 'pcs', 'nos'];
const emptyForm = {
  isSubcategory: false,
  // For variants we need both ids on save:
  //   parentItemId → ItemStoreMapping.ItemId
  //   subcategoryId → ItemStoreMapping.SubcategoryId
  // For regular menu items only itemId is used.
  itemId: '',
  parentItemId: '',
  subcategoryId: '',
  storeItemId: '',
  consumptionQty: '',
  unit: 'ml',
  activeStatus: true,
};

// Helper: defensively read a field that the API might send in different casings
// (parentItemId / ParentItemId / parentId / itemId). This was the source of the
// 400 — `parseInt(undefined)` → NaN → JSON `null` → backend rejects ItemId.
const pickParentItemId = (sub) =>
  sub?.parentItemId ??
  sub?.ParentItemId ??
  sub?.parentId ??
  sub?.itemId ??
  null;

// Helper: surface real backend validation errors instead of a generic message.
const extractApiError = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.errors && typeof data.errors === 'object') {
    // ASP.NET ValidationProblemDetails shape: { errors: { Field: ["..."] } }
    return Object.entries(data.errors)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
      .join(' | ');
  }
  return data.title || data.message || data.detail || fallback;
};

export default function ItemStoreMappingPage() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');

  const [menuItems, setMenuItems] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [storeItems, setStoreItems] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [menuRes, storeRes, mapRes, subRes] = await Promise.all([
          axios.get(
            `${config.API_BASE_URL}/Home/GetallItemsAsync/${company.id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(`${config.API_BASE_URL}/home/GetStoreItems/${company.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${config.API_BASE_URL}/home/GetMappings/${company.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(
            `${config.API_BASE_URL}/home/GetSubcategories/${company.id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);
        setMenuItems(menuRes.data || []);
        setStoreItems(storeRes.data || []);
        setMappings(mapRes.data || []);
        setSubcategories(subRes.data || []);
      } catch (err) {
        console.error('Load failed:', err?.response?.data || err);
        setAlert({
          type: 'error',
          msg: extractApiError(err, 'Failed to load data'),
        });
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  const refetchMappings = async () => {
    const res = await axios.get(
      `${config.API_BASE_URL}/home/GetMappings/${company.id}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setMappings(res.data || []);
  };

  const save = async () => {
    const needsConsumption = !!form.consumptionQty;
    const variantPicked = form.isSubcategory
      ? !!form.subcategoryId
      : !!form.itemId;
    if (!variantPicked || !form.storeItemId) {
      return setAlert({
        type: 'warning',
        msg: 'Pick the menu item / variant and the stock item.',
      });
    }
    setSaving(true);
    try {
      // For variants persist {ItemId=parent, SubcategoryId=variant, IsSubcategory=true}.
      // For menu items persist {ItemId=menu, SubcategoryId=0, IsSubcategory=false}.
      let itemId, subcategoryId;
      if (form.isSubcategory) {
        const sub = subcategories.find(
          (s) => String(s.id) === String(form.subcategoryId),
        );
        if (!sub) {
          setSaving(false);
          return setAlert({ type: 'warning', msg: 'Variant not found' });
        }
        const parent = pickParentItemId(sub);
        if (parent == null) {
          console.error('Subcategory missing parent id. Subcategory:', sub);
          setSaving(false);
          return setAlert({
            type: 'error',
            msg: 'This variant has no parent menu item id. Check the GetSubcategories response.',
          });
        }
        itemId = parseInt(parent, 10);
        subcategoryId = parseInt(sub.id, 10);
      } else {
        itemId = parseInt(form.itemId, 10);
        subcategoryId = 0;
      }

      const storeItemId = parseInt(form.storeItemId, 10);

      // Final sanity check — prevents sending `null` for any int field.
      if (
        Number.isNaN(itemId) ||
        Number.isNaN(subcategoryId) ||
        Number.isNaN(storeItemId) ||
        !company.id
      ) {
        setSaving(false);
        return setAlert({
          type: 'error',
          msg: `Bad numeric ids — companyId=${company.id}, itemId=${itemId}, subcategoryId=${subcategoryId}, storeItemId=${storeItemId}`,
        });
      }

      const unit = (form.unit || 'ml').trim();

      const payload = {
        companyId: parseInt(company.id, 10),
        itemId,
        subcategoryId,
        isSubcategory: !!form.isSubcategory,
        storeItemId,
        // Variants typically inherit consumption from the variant definition,
        // so the field is optional. Backend falls back to ItemSubcategory.ConsumptionQty
        // when this is 0.
        consumptionQty: needsConsumption ? parseFloat(form.consumptionQty) : 0,
        unit,
        activeStatus: true,
      };

      console.log('AddMapping payload:', payload);

      await axios.post(`${config.API_BASE_URL}/home/AddMapping`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlert({ type: 'success', msg: 'Mapping added!' });
      setForm(emptyForm);
      refetchMappings();
    } catch (err) {
      console.error('AddMapping failed:', err?.response?.data || err);
      setAlert({
        type: 'error',
        msg: extractApiError(err, 'Failed to save'),
      });
    }
    setSaving(false);
  };

  const toggle = async (id) => {
    try {
      await axios.get(`${config.API_BASE_URL}/home/ToggleMapping/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      refetchMappings();
    } catch (err) {
      console.error('Toggle failed:', err?.response?.data || err);
      setAlert({ type: 'error', msg: extractApiError(err, 'Toggle failed') });
    }
  };

  const parentNameById = React.useMemo(() => {
    const m = {};
    menuItems.forEach((i) => {
      m[i.id] = i.name;
    });
    return m;
  }, [menuItems]);

  const selectedStore = storeItems.find(
    (s) => String(s.id) === String(form.storeItemId),
  );
  const selectedSub = subcategories.find(
    (s) => String(s.id) === String(form.subcategoryId),
  );
  const selectedMenu = menuItems.find(
    (s) => String(s.id) === String(form.itemId),
  );

  // Auto-suggest the consumption qty from the variant's own definition so the
  // user doesn't have to retype it. They can still override.
  useEffect(() => {
    if (form.isSubcategory && selectedSub) {
      setForm((f) => ({
        ...f,
        consumptionQty:
          f.consumptionQty || String(selectedSub.consumptionQty || ''),
        unit: f.unit || selectedSub.consumptionUnit || 'ml',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subcategoryId]);

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 4 }}>
        <CardContent>
          <Typography variant='h6' fontWeight={700} color='primary' mb={1}>
            Link a Menu Item / Variant to a Stock Item
          </Typography>
          <Typography variant='body2' color='text.secondary' mb={2}>
            Tell the system <b>what to deduct from stock</b> when this menu item
            is sold. Example: <i>Signature Whiskey → 30 ml Peg</i> deducts{' '}
            <b>30 ml</b> from the <i>Whiskey Bottle</i> stock automatically.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box display='flex' flexWrap='wrap' gap={2} alignItems='center'>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isSubcategory}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      isSubcategory: e.target.checked,
                      itemId: '',
                      subcategoryId: '',
                      consumptionQty: '',
                      unit: 'ml',
                    })
                  }
                />
              }
              label={
                form.isSubcategory
                  ? 'Variant (e.g. 30 ml Peg)'
                  : 'Menu item (e.g. Chicken Biryani)'
              }
            />
            {form.isSubcategory ? (
              <FormControl size='small' sx={{ minWidth: 300 }}>
                <InputLabel>Food / Menu Item — Variant *</InputLabel>
                <Select
                  label='Food / Menu Item — Variant *'
                  value={form.subcategoryId}
                  onChange={(e) =>
                    setForm({ ...form, subcategoryId: e.target.value })
                  }>
                  {subcategories.map((i) => {
                    const parentId = pickParentItemId(i);
                    return (
                      <MenuItem key={i.id} value={i.id}>
                        {(parentNameById[parentId] ||
                          i.parentItemName ||
                          'Item') +
                          ' → ' +
                          i.subcategoryName +
                          ` (${i.consumptionQty} ${i.consumptionUnit})`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            ) : (
              <FormControl size='small' sx={{ minWidth: 300 }}>
                <InputLabel>
                  Food / Menu Item (e.g. Chicken Biryani) *
                </InputLabel>
                <Select
                  label='Food / Menu Item (e.g. Chicken Biryani) *'
                  value={form.itemId}
                  onChange={(e) =>
                    setForm({ ...form, itemId: e.target.value })
                  }>
                  {menuItems.map((i) => (
                    <MenuItem key={i.id} value={i.id}>
                      {i.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size='small' sx={{ minWidth: 280 }}>
              <InputLabel>
                Stock Item From Store (e.g. Whiskey Bottle) *
              </InputLabel>
              <Select
                label='Stock Item From Store (e.g. Whiskey Bottle) *'
                value={form.storeItemId}
                onChange={(e) =>
                  setForm({ ...form, storeItemId: e.target.value })
                }>
                {storeItems
                  .filter((s) => s.activeStatus)
                  .map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.storeName} (
                      {Number(s.currentStock).toFixed(
                        s.unit === 'bottle' ? 2 : 0,
                      )}{' '}
                      {s.unit} available
                      {s.unit === 'bottle' && s.bottleSize
                        ? ` · ${s.bottleSize} ${s.bottleUnit || 'ml'}/bottle`
                        : ''}
                      )
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label='Quantity consumed per order'
              size='small'
              type='number'
              value={form.consumptionQty}
              onChange={(e) =>
                setForm({ ...form, consumptionQty: e.target.value })
              }
              helperText={
                form.isSubcategory
                  ? 'Leave blank to use the variant default'
                  : 'e.g. 100 (grams of chicken per biryani)'
              }
              sx={{ minWidth: 200 }}
            />
            <TextField
              select
              label='Unit of consumption'
              size='small'
              value={form.unit || 'ml'}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              sx={{ minWidth: 160 }}
              SelectProps={{ native: true }}>
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </TextField>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={save}
              disabled={saving}>
              {saving ? 'Saving...' : 'Add Mapping'}
            </Button>
          </Box>
          {(selectedSub || selectedMenu) && selectedStore && (
            <Alert severity='success' sx={{ mt: 2 }}>
              <b>Preview:</b> 1 order of{' '}
              <i>
                {selectedSub
                  ? `${parentNameById[pickParentItemId(selectedSub)] || 'Item'} → ${selectedSub.subcategoryName}`
                  : selectedMenu?.name}
              </i>{' '}
              will deduct{' '}
              <b>
                {form.consumptionQty ||
                  (selectedSub ? selectedSub.consumptionQty : '?')}{' '}
                {form.unit || (selectedSub ? selectedSub.consumptionUnit : '')}
              </b>{' '}
              from <i>{selectedStore.storeName}</i>
              {selectedStore.unit === 'bottle' && selectedStore.bottleSize
                ? ` (= ${(
                    (Number(form.consumptionQty) ||
                      Number(selectedSub?.consumptionQty || 0)) /
                    Number(selectedStore.bottleSize)
                  ).toFixed(4)} bottle)`
                : ''}
              .
            </Alert>
          )}
          <Alert severity='info' sx={{ mt: 2 }}>
            <b>How this works:</b> If 1 order of <i>30 ml Peg</i> is sold, the
            system deducts <b>30 ml</b> from the <i>Whiskey Bottle</i> stock. If
            the store unit is <i>bottle</i> (e.g. 750 ml/bottle), the system
            converts automatically — 4 × 30 ml = 120 ml ≈ 0.16 bottle.
          </Alert>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Typography variant='h6' fontWeight={600} mb={2}>
            All Mappings
          </Typography>
          {loading ? (
            <Box textAlign='center' py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {[
                      '#',
                      'Food / Menu Item',
                      'Type',
                      'Stock Item From Store',
                      'Qty consumed per order',
                      'Status',
                      'Actions',
                    ].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((m, i) => {
                    const label = m.subcategoryName
                      ? `${m.itemName || 'Item'} → ${m.subcategoryName}`
                      : m.itemName || `#${m.itemId}`;
                    const isVariant = !!m.subcategoryName || !!m.isSubcategory;
                    return (
                      <TableRow key={m.id} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                        <TableCell>
                          <Chip
                            label={isVariant ? 'Variant' : 'Menu Item'}
                            size='small'
                            color={isVariant ? 'secondary' : 'primary'}
                            variant='outlined'
                          />
                        </TableCell>
                        <TableCell>
                          {m.storeItemName || `#${m.storeItemId}`}
                          {m.storeItemUnit ? (
                            <Typography
                              variant='caption'
                              display='block'
                              color='text.secondary'>
                              stock unit: {m.storeItemUnit}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {m.consumptionQty > 0
                            ? `${m.consumptionQty} ${m.unit}`
                            : 'auto (from variant)'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={m.activeStatus ? 'Active' : 'Inactive'}
                            color={m.activeStatus ? 'success' : 'error'}
                            size='small'
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size='small'
                            variant='outlined'
                            color={m.activeStatus ? 'warning' : 'success'}
                            onClick={() => toggle(m.id)}>
                            {m.activeStatus ? 'Disable' : 'Enable'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
